"use client";
import { useState } from "react";
import Link from "next/link";
import { ArrowLeft, Calendar, Plus, Minus } from "lucide-react";
import { OptimizedImage } from "@/components/common/OptimizedImage";
import { cleanHeaderTags } from "@/lib/helper";

interface BlogDetailsProps {
  blogUrl: string;
  blogData: any;
}

export default function BlogDetails({ blogUrl, blogData }: BlogDetailsProps) {
  const BlogDetailsData = blogData;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!BlogDetailsData) {
    return (
      <div className="min-h-screen py-32 text-center">Blog post not found.</div>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-end mt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80 z-10" />
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src={BlogDetailsData?.BlogBannerImage || BlogDetailsData?.Image}
            alt={BlogDetailsData?.BlogTitle || "Blog Post"}
            fill
            priority
            className="object-cover"
          />
        </div>
        <div className="relative z-20 w-full max-w-6xl mx-auto px-6 pb-16">
          <Link
            href="/blogs/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blogs
          </Link>
          <div className="flex items-center gap-4 text-sm text-white/80 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {BlogDetailsData.BlogDate ? BlogDetailsData.BlogDate : "Recent"}
            </div>
          </div>
          <h1 className="text-white drop-shadow-2xl">
            {BlogDetailsData?.BlogTitle}
          </h1>
        </div>
      </section>

      {/* Blog Content */}
      <article className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <div
              className="text-justify cms-section blog-desc"
              dangerouslySetInnerHTML={{
                __html: cleanHeaderTags(BlogDetailsData.LongDescription),
              }}
            />
          </div>

          {/* FAQ Section */}
          {BlogDetailsData.BlogFAQList &&
            BlogDetailsData.BlogFAQList.length > 0 && (
              <div className="mt-16 pt-10 border-t border-[#D8C4A8]/30 max-w-4xl mx-auto">
                <h2 className="text-[#5A3A1E] text-3xl font-bold mb-8">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {BlogDetailsData.BlogFAQList.map(
                    (faq: any, index: number) => {
                      const isOpen = openIndex === index;
                      return (
                        <div
                          key={faq.ID || index}
                          className="bg-[#F3EBDD]/30 rounded-2xl overflow-hidden border border-[#D8C4A8]/30"
                        >
                          <button
                            onClick={() => setOpenIndex(isOpen ? null : index)}
                            className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-[#F3EBDD]/50 transition-colors cursor-pointer"
                          >
                            <span className="text-[#5A3A1E] font-medium pr-8">
                              {faq.Question}
                            </span>
                            {isOpen ? (
                              <Minus className="w-5 h-5 text-[#BF5500] flex-shrink-0" />
                            ) : (
                              <Plus className="w-5 h-5 text-[#BF5500] flex-shrink-0" />
                            )}
                          </button>
                          {isOpen && (
                            <div className="px-8 py-6 text-[#5A3A1E]/85 leading-relaxed">
                              {faq.Answer}
                            </div>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            )}
        </div>
      </article>
    </>
  );
}
